import React, { useEffect, useMemo, useState } from "react";
import { X, Download, Loader2, AlertTriangle, Check, Layers, ScissorsLineDashed, Maximize2 } from "lucide-react";
import api, { formatApiErrorDetail, BACKEND_URL } from "@/lib/api";

/**
 * SmartExportModal
 * --------------------------------------------------------------
 * "Create once, export everywhere." Lets the user pick a target
 * platform preset (Meta Feed / Stories / Reels / TikTok /
 * YouTube Shorts / YouTube Landscape / Bumper) and re-encode an
 * existing render to that platform's exact spec.
 *
 * Props:
 *   open            - bool, controls visibility
 *   onClose         - fn
 *   renderId        - string, the source render to export
 *   sourceAspect    - "9:16" | "1:1" | "16:9" | "4:5" | undefined
 *                     used to surface aspect-mismatch warnings
 *   sourceKind      - "image" | "video"
 */
export default function SmartExportModal({ open, onClose, renderId, sourceAspect, sourceKind = "video" }) {
  const [presets, setPresets] = useState([]);
  const [chosen,  setChosen]  = useState(null);
  const [fit,     setFit]     = useState("fit");
  const [busy,    setBusy]    = useState(false);
  const [result,  setResult]  = useState(null);
  const [error,   setError]   = useState("");

  useEffect(() => {
    if (!open) return;
    setResult(null); setError(""); setChosen(null);
    api.get("/exports/presets")
      .then(({ data }) => setPresets(data?.presets || []))
      .catch(() => setError("Couldn't load export presets."));
  }, [open]);

  const grouped = useMemo(() => {
    const byPlatform = { meta: [], tiktok: [], youtube: [], ctv: [] };
    for (const p of presets) {
      if (!byPlatform[p.platform]) byPlatform[p.platform] = [];
      byPlatform[p.platform].push(p);
    }
    return byPlatform;
  }, [presets]);

  if (!open) return null;

  const onExport = async () => {
    if (!chosen) return;
    setBusy(true); setError(""); setResult(null);
    try {
      const { data } = await api.post(`/exports/${renderId}`, {
        preset_id: chosen.id,
        fit,
      });
      setResult(data);
    } catch (err) {
      setError(formatApiErrorDetail(err) || "Export failed — please retry.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="smart-export-modal"
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 bg-black/80 flex items-start sm:items-center justify-center p-4 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="bg-[#0A0A0A] border border-[#1F1F1F] w-full max-w-3xl my-8"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between px-6 py-4 border-b border-[#1F1F1F]">
          <div>
            <p className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37]">Smart Export</p>
            <h2 className="font-display text-2xl font-bold text-[#F4F4F0]">Create once, export everywhere</h2>
          </div>
          <button onClick={onClose} data-testid="export-close" className="text-[#A0A0A0] hover:text-[#F4F4F0] p-2"><X className="w-5 h-5" /></button>
        </header>

        {!result && (
          <div className="px-6 py-5 space-y-6">
            <p className="text-xs text-[#A0A0A0] leading-relaxed">
              Pick a destination. We'll re-render at the exact size, frame-rate and bitrate that platform expects — and warn you when your source aspect doesn't match.
            </p>

            {Object.entries(grouped).map(([platform, list]) => list.length === 0 ? null : (
              <section key={platform}>
                <p className="text-[10px] uppercase tracking-[0.22em] text-[#A0A0A0] mb-2">{PLATFORM_LABEL[platform] || platform}</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid={`export-group-${platform}`}>
                  {list.map((p) => (
                    <PresetCard
                      key={p.id}
                      preset={p}
                      selected={chosen?.id === p.id}
                      sourceAspect={sourceAspect}
                      onClick={() => !p.staged && setChosen(p)}
                    />
                  ))}
                </div>
              </section>
            ))}

            {/* Fit mode */}
            {chosen && (
              <div className="border border-[#1F1F1F] bg-[#080808] p-4">
                <p className="text-[10px] uppercase tracking-[0.22em] text-[#A0A0A0] mb-3">How should we fit the source into {chosen.label}?</p>
                <div className="grid grid-cols-2 gap-3">
                  <FitChoice
                    active={fit === "fit"}
                    onClick={() => setFit("fit")}
                    icon={<Maximize2 className="w-4 h-4" />}
                    title="Letterbox (recommended)"
                    desc="Keep the full frame. Add black bars where the source doesn't fill."
                    testId="export-fit-letterbox"
                  />
                  <FitChoice
                    active={fit === "fill"}
                    onClick={() => setFit("fill")}
                    icon={<ScissorsLineDashed className="w-4 h-4" />}
                    title="Crop to fill"
                    desc="Zoom and center-crop to fill the frame. Source edges will be cut."
                    testId="export-fit-crop"
                  />
                </div>

                {/* Aspect-mismatch warning */}
                {sourceAspect && sourceAspect !== chosen.aspect && (
                  <div
                    data-testid="export-aspect-warning"
                    className="mt-3 px-3 py-2 border border-amber-500/40 bg-amber-500/10 text-amber-300 text-xs flex items-start gap-2"
                  >
                    <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                    <span>
                      Your source is <b>{sourceAspect}</b> but {chosen.label} expects <b>{chosen.aspect}</b>.
                      Letterbox preserves the full frame; Crop reframes it.
                    </span>
                  </div>
                )}
              </div>
            )}

            {error && (
              <div data-testid="export-error" className="px-3 py-2 border border-red-500/40 bg-red-500/10 text-red-300 text-xs">{error}</div>
            )}

            <button
              onClick={onExport}
              disabled={!chosen || busy}
              data-testid="export-run-button"
              className="w-full inline-flex items-center justify-center gap-2 bg-[#D4AF37] hover:bg-[#F0C84B] text-[#080808] font-bold py-3 text-sm uppercase tracking-wider transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {busy
                ? <><Loader2 className="w-4 h-4 animate-spin" /> Encoding for {chosen?.label}…</>
                : chosen
                  ? <><Layers className="w-4 h-4" /> Export {chosen.label}</>
                  : "Pick a destination above"}
            </button>
            <p className="text-[10px] text-[#5A5A5A] uppercase tracking-wider text-center">
              Exports are free — credits are only charged at generation time.
            </p>
          </div>
        )}

        {result && (
          <div className="px-6 py-6" data-testid="export-result">
            <div className="border border-emerald-500/30 bg-emerald-500/5 p-5 text-center">
              <Check className="w-8 h-8 text-emerald-400 mx-auto mb-3" />
              <p className="font-display text-xl font-bold text-[#F4F4F0]">
                {result.preset?.badge || "Export ready"}
              </p>
              <p className="text-xs text-[#A0A0A0] mt-1">
                {result.preset?.width}×{result.preset?.height} · {result.preset?.aspect} · {result.preset?.container?.toUpperCase()}
              </p>
              <p className="text-[10px] text-[#5A5A5A] uppercase tracking-wider mt-2">
                Encoded in {(result.latency_ms / 1000).toFixed(1)}s
              </p>
            </div>
            <a
              href={`${BACKEND_URL}${result.download_url}`}
              data-testid="export-download-button"
              className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-[#D4AF37] hover:bg-[#F0C84B] text-[#080808] font-bold py-3 text-sm uppercase tracking-wider transition-colors"
            >
              <Download className="w-4 h-4" /> Download {result.preset?.container?.toUpperCase()}
            </a>
            <button
              onClick={() => { setResult(null); setChosen(null); }}
              data-testid="export-another"
              className="mt-3 w-full text-[10px] uppercase tracking-wider text-[#A0A0A0] hover:text-[#D4AF37] py-2"
            >
              Export another format
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

const PLATFORM_LABEL = {
  meta:    "Meta · Facebook / Instagram",
  tiktok:  "TikTok",
  youtube: "YouTube",
  ctv:     "CTV / OTT",
};

function PresetCard({ preset, selected, sourceAspect, onClick }) {
  const mismatch = sourceAspect && sourceAspect !== preset.aspect;
  const staged = preset.staged;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={staged}
      data-testid={`export-preset-${preset.id}`}
      className={`relative text-left px-4 py-3 border transition-colors ${
        staged
          ? "border-[#1F1F1F] bg-[#0A0A0A] opacity-50 cursor-not-allowed"
          : selected
            ? "border-[#D4AF37] bg-[#D4AF37]/5"
            : "border-[#1F1F1F] hover:border-[#D4AF37]/50 bg-[#080808]"
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="font-display text-sm font-bold text-[#F4F4F0]">{preset.label}</p>
          <p className="text-[10px] text-[#A0A0A0] mt-0.5 truncate">
            {preset.width}×{preset.height} · {preset.aspect}
            {preset.duration_rec && ` · ~${preset.duration_rec}s`}
          </p>
        </div>
        {selected && !staged && <Check className="w-4 h-4 text-[#D4AF37] mt-0.5 shrink-0" />}
        {staged && <span className="text-[8px] uppercase tracking-wider text-[#5A5A5A] mt-0.5 whitespace-nowrap">Soon</span>}
      </div>
      <p className="text-[10px] text-[#5A5A5A] mt-2 leading-relaxed line-clamp-2">{preset.notes}</p>
      {mismatch && !staged && (
        <p className="text-[10px] text-amber-300 mt-2 flex items-center gap-1">
          <AlertTriangle className="w-3 h-3" /> Source aspect mismatch
        </p>
      )}
    </button>
  );
}

function FitChoice({ active, onClick, icon, title, desc, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`text-left px-3 py-2.5 border transition-colors ${
        active ? "border-[#D4AF37] bg-[#D4AF37]/5" : "border-[#1F1F1F] hover:border-[#D4AF37]/50 bg-[#080808]"
      }`}
    >
      <div className="flex items-center gap-2 text-[#F4F4F0]">{icon} <span className="text-xs font-bold">{title}</span></div>
      <p className="text-[10px] text-[#A0A0A0] mt-1 leading-relaxed">{desc}</p>
    </button>
  );
}
